import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ScrollArea } from '../../components/ui/scroll-area';
import { 
  Calendar, RefreshCw, AlertTriangle, CheckCircle, Users, Stethoscope,
  Copy, CalendarRange, FileText
} from 'lucide-react';
import { format, addDays, startOfWeek, parseISO } from 'date-fns';
import { toast } from 'sonner';

// Weekday shifts (Monday-Friday)
const WEEKDAY_SHIFTS = [
  { value: 'AM', label: 'AM Shift', start: '09:00', end: '17:00', target: 2 },
  { value: 'Closing', label: 'Closing Shift', start: '11:30', end: '19:30', target: 2 }
];

// Saturday shift
const SATURDAY_SHIFT = { value: 'Saturday', label: 'Saturday Shift', start: '08:30', end: '12:30', target: 2 };

// Combined for display
const ALL_SHIFT_TYPES = [...WEEKDAY_SHIFTS, SATURDAY_SHIFT];

const WEEKDAYS = [
  { value: 0, label: 'Mon' },
  { value: 1, label: 'Tue' },
  { value: 2, label: 'Wed' },
  { value: 3, label: 'Thu' },
  { value: 4, label: 'Fri' },
  { value: 5, label: 'Sat' },
  { value: 6, label: 'Sun' }
];

const REPEAT_PATTERNS = [
  { value: 'weekdays', label: 'Every Weekday (Mon-Fri)' },
  { value: 'mon_fri', label: 'Monday to Friday' },
  { value: 'saturdays', label: 'Saturdays Only' },
  { value: 'selected_days', label: 'Selected Days' },
  { value: 'daily', label: 'Every Day' }
];

export default function BulkScheduleDialog({ 
  open, 
  onOpenChange, 
  scheduleType = 'physician', 
  onSuccess,
  currentDate 
}) {
  const { api } = useAuth();
  const [activeTab, setActiveTab] = useState('bulk');
  const [loading, setLoading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  
  // Data lists
  const [physicians, setPhysicians] = useState([]);
  const [employees, setEmployees] = useState([]);
  
  // Physician bulk form
  const [physicianForm, setPhysicianForm] = useState({
    physician_id: '',
    event_type: 'PHYSICIAN_STANDARD',
    start_date: format(currentDate || new Date(), 'yyyy-MM-dd'),
    end_date: format(addDays(currentDate || new Date(), 30), 'yyyy-MM-dd'),
    start_time: '09:00',
    end_time: '17:00',
    repeat_pattern: 'weekdays',
    selected_days: [0, 1, 2, 3, 4],
    overwrite_existing: false,
    notes: ''
  });

  // Employee bulk form
  const [employeeForm, setEmployeeForm] = useState({
    start_date: format(currentDate || new Date(), 'yyyy-MM-dd'),
    end_date: format(addDays(currentDate || new Date(), 30), 'yyyy-MM-dd'),
    repeat_pattern: 'weekdays',
    selected_days: [0, 1, 2, 3, 4],
    overwrite_existing: false,
    shifts: []
  });

  // Multi-employee day form - Updated for new shift structure
  const [multiDayForm, setMultiDayForm] = useState({
    date: format(currentDate || new Date(), 'yyyy-MM-dd'),
    am_employees: [],
    closing_employees: [],
    saturday_employees: [],
    am_times: { start: '09:00', end: '17:00' },
    closing_times: { start: '11:30', end: '19:30' },
    saturday_times: { start: '08:30', end: '12:30' },
    overwrite_existing: false
  });

  useEffect(() => {
    if (open) {
      fetchUsers();
    }
  }, [open]);

  useEffect(() => {
    // Update times based on event type
    if (physicianForm.event_type === 'PHYSICIAN_STANDARD') {
      setPhysicianForm(prev => ({ ...prev, start_time: '09:00', end_time: '17:00' }));
    } else if (physicianForm.event_type === 'PHYSICIAN_AFTERHOURS_WEEKDAY') {
      setPhysicianForm(prev => ({ ...prev, start_time: '17:00', end_time: '19:30' }));
    } else if (physicianForm.event_type === 'PHYSICIAN_AFTERHOURS_SATURDAY') {
      setPhysicianForm(prev => ({ ...prev, start_time: '08:30', end_time: '12:30' }));
    }
  }, [physicianForm.event_type]);

  const fetchUsers = async () => {
    try {
      const res = await api.get('/users?status=active');
      setPhysicians(res.data.filter(u => u.role === 'physician'));
      setEmployees(res.data.filter(u => u.role === 'employee'));
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const handlePhysicianDayToggle = (day) => {
    setPhysicianForm(prev => ({
      ...prev,
      selected_days: prev.selected_days.includes(day)
        ? prev.selected_days.filter(d => d !== day)
        : [...prev.selected_days, day].sort()
    }));
  };

  const handlePreviewPhysician = async () => {
    if (!physicianForm.physician_id) {
      toast.error('Please select a physician');
      return;
    }
    
    setPreviewing(true);
    try {
      const res = await api.post('/schedules/bulk/physician/preview', physicianForm);
      setPreviewData(res.data);
    } catch (error) {
      toast.error('Failed to generate preview');
    } finally {
      setPreviewing(false);
    }
  };

  const handleCreatePhysicianBulk = async () => {
    if (!physicianForm.physician_id) {
      toast.error('Please select a physician');
      return;
    }
    
    setLoading(true);
    try {
      const res = await api.post('/schedules/bulk/physician', physicianForm);
      toast.success(`Created ${res.data.created} schedule entries`);
      if (res.data.skipped > 0) {
        toast.info(`Skipped ${res.data.skipped} dates due to conflicts`);
      }
      setPreviewData(null);
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create schedules');
    } finally {
      setLoading(false);
    }
  };

  const handleMultiEmployeeDay = async () => {
    if (multiDayForm.am_employees.length === 0 && 
        multiDayForm.closing_employees.length === 0 && 
        multiDayForm.saturday_employees.length === 0) {
      toast.error('Please select at least one employee');
      return;
    }
    
    setLoading(true);
    try {
      const res = await api.post('/schedules/multi-employee-day', multiDayForm);
      toast.success(res.data.message);
      if (res.data.skipped?.length > 0) {
        toast.info(`Skipped ${res.data.skipped.length} shifts due to conflicts`);
      }
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create schedules');
    } finally {
      setLoading(false);
    }
  };

  const handleEmployeeShiftToggle = (employeeId, shiftBucket) => {
    setEmployeeForm(prev => {
      const existing = prev.shifts.find(s => s.employee_id === employeeId && s.shift_bucket === shiftBucket);
      if (existing) {
        return {
          ...prev,
          shifts: prev.shifts.filter(s => !(s.employee_id === employeeId && s.shift_bucket === shiftBucket))
        };
      } else {
        const shift = ALL_SHIFT_TYPES.find(b => b.value === shiftBucket);
        return {
          ...prev,
          shifts: [...prev.shifts, {
            employee_id: employeeId,
            shift_bucket: shiftBucket,
            start_time: shift?.start || '09:00',
            end_time: shift?.end || '17:00'
          }]
        };
      }
    });
  };

  const isEmployeeShiftSelected = (employeeId, shiftBucket) => {
    return employeeForm.shifts.some(s => s.employee_id === employeeId && s.shift_bucket === shiftBucket);
  };

  const handleCreateEmployeeBulk = async () => {
    if (employeeForm.shifts.length === 0) {
      toast.error('Please select at least one employee shift');
      return;
    }
    
    setLoading(true);
    try {
      const res = await api.post('/schedules/bulk/employee', employeeForm);
      toast.success(`Created ${res.data.created} schedule entries`);
      if (res.data.skipped > 0) {
        toast.info(`Skipped ${res.data.skipped} due to conflicts`);
      }
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create schedules');
    } finally {
      setLoading(false);
    }
  };

  const toggleMultiDayEmployee = (shiftType, employeeId) => {
    const key = `${shiftType}_employees`;
    setMultiDayForm(prev => ({
      ...prev,
      [key]: prev[key].includes(employeeId)
        ? prev[key].filter(id => id !== employeeId)
        : [...prev[key], employeeId]
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarRange className="h-5 w-5 text-teal-600" />
            Bulk Schedule Creator
          </DialogTitle>
          <DialogDescription>
            Create multiple schedule entries at once for {scheduleType === 'physician' ? 'physicians' : 'employees'}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="bulk" className="flex items-center gap-2">
              <CalendarRange className="h-4 w-4" />
              Bulk Schedule
            </TabsTrigger>
            <TabsTrigger value="day" className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Single Day
            </TabsTrigger>
            <TabsTrigger value="preview" className="flex items-center gap-2" disabled={!previewData}>
              <FileText className="h-4 w-4" />
              Preview
            </TabsTrigger>
          </TabsList>

          <ScrollArea className="flex-1 pr-4 mt-4">
            {/* Bulk Schedule Tab */}
            <TabsContent value="bulk" className="space-y-4 mt-0">
              {scheduleType === 'physician' ? (
                <>
                  {/* Physician Selection */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Physician *</Label>
                      <Select 
                        value={physicianForm.physician_id} 
                        onValueChange={(v) => setPhysicianForm(prev => ({ ...prev, physician_id: v }))}
                      >
                        <SelectTrigger data-testid="bulk-physician-select">
                          <SelectValue placeholder="Select physician" />
                        </SelectTrigger>
                        <SelectContent>
                          {physicians.map(p => (
                            <SelectItem key={p.id} value={p.id}>
                              Dr. {p.last_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Schedule Type</Label>
                      <Select 
                        value={physicianForm.event_type} 
                        onValueChange={(v) => setPhysicianForm(prev => ({ ...prev, event_type: v }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="PHYSICIAN_STANDARD">Standard (9am-5pm)</SelectItem>
                          <SelectItem value="PHYSICIAN_AFTERHOURS_WEEKDAY">After-Hours Weekday (5pm-7:30pm)</SelectItem>
                          <SelectItem value="PHYSICIAN_AFTERHOURS_SATURDAY">After-Hours Saturday (9am-12pm)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Date Range */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Start Date</Label>
                      <Input 
                        type="date" 
                        value={physicianForm.start_date}
                        onChange={(e) => setPhysicianForm(prev => ({ ...prev, start_date: e.target.value }))}
                        data-testid="bulk-start-date"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>End Date</Label>
                      <Input 
                        type="date" 
                        value={physicianForm.end_date}
                        onChange={(e) => setPhysicianForm(prev => ({ ...prev, end_date: e.target.value }))}
                        data-testid="bulk-end-date"
                      />
                    </div>
                  </div>

                  {/* Repeat Pattern */}
                  <div className="space-y-2">
                    <Label>Repeat Pattern</Label>
                    <Select 
                      value={physicianForm.repeat_pattern} 
                      onValueChange={(v) => setPhysicianForm(prev => ({ ...prev, repeat_pattern: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {REPEAT_PATTERNS.map(p => (
                          <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Selected Days */}
                  {physicianForm.repeat_pattern === 'selected_days' && (
                    <div className="space-y-2">
                      <Label>Select Days</Label>
                      <div className="flex gap-2 flex-wrap">
                        {WEEKDAYS.map(day => (
                          <Button
                            key={day.value}
                            type="button"
                            variant={physicianForm.selected_days.includes(day.value) ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => handlePhysicianDayToggle(day.value)}
                          >
                            {day.label}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Time Override */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Start Time</Label>
                      <Input 
                        type="time" 
                        value={physicianForm.start_time}
                        onChange={(e) => setPhysicianForm(prev => ({ ...prev, start_time: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>End Time</Label>
                      <Input 
                        type="time" 
                        value={physicianForm.end_time}
                        onChange={(e) => setPhysicianForm(prev => ({ ...prev, end_time: e.target.value }))}
                      />
                    </div>
                  </div>

                  {/* Overwrite Option */}
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium text-sm">Overwrite Existing</p>
                      <p className="text-xs text-slate-500">Replace schedules if dates are already occupied</p>
                    </div>
                    <Switch 
                      checked={physicianForm.overwrite_existing}
                      onCheckedChange={(v) => setPhysicianForm(prev => ({ ...prev, overwrite_existing: v }))}
                    />
                  </div>
                </>
              ) : (
                <>
                  {/* Employee Bulk Scheduling */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Start Date</Label>
                      <Input 
                        type="date" 
                        value={employeeForm.start_date}
                        onChange={(e) => setEmployeeForm(prev => ({ ...prev, start_date: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>End Date</Label>
                      <Input 
                        type="date" 
                        value={employeeForm.end_date}
                        onChange={(e) => setEmployeeForm(prev => ({ ...prev, end_date: e.target.value }))}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Repeat Pattern</Label>
                    <Select 
                      value={employeeForm.repeat_pattern} 
                      onValueChange={(v) => setEmployeeForm(prev => ({ ...prev, repeat_pattern: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {REPEAT_PATTERNS.map(p => (
                          <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Employee Shift Assignment Table */}
                  <div className="space-y-2">
                    <Label>Assign Employees to Shifts</Label>
                    <p className="text-xs text-slate-500">
                      AM: 9:00-17:00 | Closing: 11:30-19:30 | Saturday: 8:30-12:30
                    </p>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="p-2 text-left font-medium">Employee</th>
                            {WEEKDAY_SHIFTS.map(shift => (
                              <th key={shift.value} className="p-2 text-center font-medium">
                                {shift.label}
                              </th>
                            ))}
                            <th className="p-2 text-center font-medium">
                              {SATURDAY_SHIFT.label}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {employees.map(emp => (
                            <tr key={emp.id} className="border-t">
                              <td className="p-2">{emp.first_name} {emp.last_name}</td>
                              {WEEKDAY_SHIFTS.map(shift => (
                                <td key={shift.value} className="p-2 text-center">
                                  <Checkbox
                                    checked={isEmployeeShiftSelected(emp.id, shift.value)}
                                    onCheckedChange={() => handleEmployeeShiftToggle(emp.id, shift.value)}
                                  />
                                </td>
                              ))}
                              <td className="p-2 text-center">
                                <Checkbox
                                  checked={isEmployeeShiftSelected(emp.id, SATURDAY_SHIFT.value)}
                                  onCheckedChange={() => handleEmployeeShiftToggle(emp.id, SATURDAY_SHIFT.value)}
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-xs text-slate-500">
                      Selected: {employeeForm.shifts.length} shift assignment(s)
                    </p>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium text-sm">Overwrite Existing</p>
                      <p className="text-xs text-slate-500">Replace schedules if dates are already occupied</p>
                    </div>
                    <Switch 
                      checked={employeeForm.overwrite_existing}
                      onCheckedChange={(v) => setEmployeeForm(prev => ({ ...prev, overwrite_existing: v }))}
                    />
                  </div>
                </>
              )}
            </TabsContent>

            {/* Single Day Tab (Multi-Employee) */}
            <TabsContent value="day" className="space-y-4 mt-0">
              <div className="space-y-2">
                <Label>Date</Label>
                <Input 
                  type="date" 
                  value={multiDayForm.date}
                  onChange={(e) => setMultiDayForm(prev => ({ ...prev, date: e.target.value }))}
                  data-testid="multi-day-date"
                />
              </div>

              {/* Weekday Shifts */}
              {WEEKDAY_SHIFTS.map(shift => (
                <div key={shift.value} className="space-y-2 p-4 border rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-base">{shift.label}</Label>
                      <p className="text-xs text-slate-500">
                        Target: {shift.target} employee(s) | {shift.start} - {shift.end}
                      </p>
                    </div>
                    <Badge variant={
                      multiDayForm[`${shift.value.toLowerCase()}_employees`]?.length >= shift.target 
                        ? 'success' 
                        : 'secondary'
                    }>
                      {multiDayForm[`${shift.value.toLowerCase()}_employees`]?.length || 0} / {shift.target}
                    </Badge>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {employees.map(emp => {
                      const key = `${shift.value.toLowerCase()}_employees`;
                      const isSelected = multiDayForm[key]?.includes(emp.id);
                      return (
                        <Button
                          key={emp.id}
                          type="button"
                          variant={isSelected ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => toggleMultiDayEmployee(shift.value.toLowerCase(), emp.id)}
                          className={isSelected ? 'bg-teal-600 hover:bg-teal-700' : ''}
                        >
                          {emp.first_name} {emp.last_name?.charAt(0)}.
                        </Button>
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* Saturday Shift - Only shown if selected date is Saturday */}
              {new Date(multiDayForm.date).getDay() === 6 && (
                <div className="space-y-2 p-4 border rounded-lg bg-violet-50/50">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-base">{SATURDAY_SHIFT.label}</Label>
                      <p className="text-xs text-slate-500">
                        Target: {SATURDAY_SHIFT.target} employee(s) | {SATURDAY_SHIFT.start} - {SATURDAY_SHIFT.end}
                      </p>
                    </div>
                    <Badge variant={
                      multiDayForm.saturday_employees?.length >= SATURDAY_SHIFT.target 
                        ? 'success' 
                        : 'secondary'
                    }>
                      {multiDayForm.saturday_employees?.length || 0} / {SATURDAY_SHIFT.target}
                    </Badge>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {employees.map(emp => {
                      const isSelected = multiDayForm.saturday_employees?.includes(emp.id);
                      return (
                        <Button
                          key={emp.id}
                          type="button"
                          variant={isSelected ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => toggleMultiDayEmployee('saturday', emp.id)}
                          className={isSelected ? 'bg-violet-600 hover:bg-violet-700' : ''}
                        >
                          {emp.first_name} {emp.last_name?.charAt(0)}.
                        </Button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-sm">Overwrite Existing</p>
                  <p className="text-xs text-slate-500">Replace if employee already scheduled</p>
                </div>
                <Switch 
                  checked={multiDayForm.overwrite_existing}
                  onCheckedChange={(v) => setMultiDayForm(prev => ({ ...prev, overwrite_existing: v }))}
                />
              </div>
            </TabsContent>

            {/* Preview Tab */}
            <TabsContent value="preview" className="space-y-4 mt-0">
              {previewData && (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 bg-slate-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-slate-900">{previewData.total_dates || previewData.total_entries}</p>
                      <p className="text-sm text-slate-500">Total Entries</p>
                    </div>
                    <div className="p-4 bg-emerald-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-emerald-600">{previewData.will_create || previewData.entries?.length}</p>
                      <p className="text-sm text-slate-500">Will Create</p>
                    </div>
                    <div className="p-4 bg-amber-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-amber-600">{previewData.conflicts?.length || 0}</p>
                      <p className="text-sm text-slate-500">Conflicts</p>
                    </div>
                  </div>

                  {previewData.conflicts?.length > 0 && (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="h-5 w-5 text-amber-600" />
                        <p className="font-medium text-amber-700">Conflict Dates</p>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {previewData.conflicts.slice(0, 10).map(date => (
                          <Badge key={date} variant="outline" className="bg-white">
                            {typeof date === 'string' ? date : date.date}
                          </Badge>
                        ))}
                        {previewData.conflicts.length > 10 && (
                          <Badge variant="outline">+{previewData.conflicts.length - 10} more</Badge>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="border rounded-lg max-h-[200px] overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          <th className="p-2 text-left">Date</th>
                          <th className="p-2 text-left">Time</th>
                          <th className="p-2 text-left">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.entries?.slice(0, 20).map((entry, idx) => (
                          <tr key={idx} className="border-t">
                            <td className="p-2">{entry.date}</td>
                            <td className="p-2">{entry.start_time} - {entry.end_time}</td>
                            <td className="p-2">
                              {entry.has_conflict ? (
                                <Badge className="bg-amber-100 text-amber-700">Conflict</Badge>
                              ) : (
                                <Badge className="bg-emerald-100 text-emerald-700">OK</Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </TabsContent>
          </ScrollArea>
        </Tabs>

        <DialogFooter className="mt-4 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          
          {activeTab === 'bulk' && (
            <>
              {scheduleType === 'physician' ? (
                <>
                  <Button 
                    variant="outline"
                    onClick={handlePreviewPhysician}
                    disabled={previewing || !physicianForm.physician_id}
                  >
                    {previewing ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
                    Preview
                  </Button>
                  <Button 
                    className="bg-teal-600 hover:bg-teal-700"
                    onClick={handleCreatePhysicianBulk}
                    disabled={loading || !physicianForm.physician_id}
                    data-testid="create-bulk-physician"
                  >
                    {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                    Create Schedules
                  </Button>
                </>
              ) : (
                <Button 
                  className="bg-teal-600 hover:bg-teal-700"
                  onClick={handleCreateEmployeeBulk}
                  disabled={loading || employeeForm.shifts.length === 0}
                  data-testid="create-bulk-employee"
                >
                  {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                  Create Schedules
                </Button>
              )}
            </>
          )}
          
          {activeTab === 'day' && (
            <Button 
              className="bg-teal-600 hover:bg-teal-700"
              onClick={handleMultiEmployeeDay}
              disabled={loading}
              data-testid="create-multi-day"
            >
              {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
              Create Day Schedule
            </Button>
          )}

          {activeTab === 'preview' && previewData && (
            <Button 
              className="bg-teal-600 hover:bg-teal-700"
              onClick={handleCreatePhysicianBulk}
              disabled={loading}
            >
              {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
              Confirm & Create
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
